export enum EventTypesColors {
  TILLAGE = '#4C9C2E',
  PLANTING = '#419DD7',
  COVER_CROP = '#D741A4',
  FERTILIZATION = '#5D452B',
  FORAGE = '#D76E41',
  FIELD_DATA = '#419DD7',
  ANIMAL_DATA = '#7623CB',
  YEAR_TAG = '#00B7AE',
}

export type DataCollectionModes = 'EDIT' | 'VIEW' | 'BULK_COPY';

export type EventTypes = 'Tillage' | 'Planting' | 'Harvest' | 'CoverCrop' | 'Fertilization' | 'Forage' | 'FieldData' | 'AnimalData';

export enum EventTypesEnum {
  TILLAGE = 'Tillage',
  PLANTING = 'Planting',
  HARVEST = 'Harvest',
  COVER_CROP = 'CoverCrop',
  FERTILIZATION = 'Fertilization',
  FORAGE = 'Forage',
  FIELD_DATA = 'FieldData',
  ANIMAL_DATA = 'AnimalData',
}

export type IEvents =
  | IDCTillageEvent
  | IDCPlantingEvent
  | IDCHarvestEvent
  | IDCCoverCroppingEvent
  | IDCFertilizerEvent
  | IDCForageEvent
  | IDCFieldDataEvent
  | IDCAnimalDataEvent;

export interface TillageType {
  id: string;
  name: string;
  daycentCode: string;
  inSeason: boolean;
}

export interface IHarvestMethods {
  id: string;
  name: string;
  daycentCode: string;
}

export interface IPlantSpecies {
  id: string;
  name: string;
  daycentCode: string;
}

export interface IUnitsOfMeasurement {
  id: string;
  name: string;
}

export interface ITerminationMethod {
  id: string;
  name: string;
  daycentCode: string;
}

export interface ICoverCropSpecies {
  id: string;
  name: string;
  daycentCode: string;
  legume: string;
  modelInputOnly: boolean;
}

export interface IFertilizerType {
  id: string;
  name: string;
  source: string;
}

export interface IFertilizerSourceOrInhibitor {
  id: string;
  name: string;
}

export interface IDCEventType {
  name: EventTypes;
  events: Record<string, IEvents>;
  constraints?: Array<IDCEventFieldConfiguration>;
}

export interface IDCTillageEvent {
  id: string;
  tillageDate: string;
  tillageTypeId: string;
  fieldId: string;
  markedAsDeleted?: boolean; //Determines if event should be deleted on save
  newEvent?: boolean; //Determines event appearance in different data collection modes
  copy?: boolean; //Determines if the event will be bulk copied
  duplicatedDate?: boolean; //Determines if there is a event with the same date in the field
  missingData?: boolean;
  erroredEvent?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IDCPlantingEvent {
  id: string;
  plantingDate: string;
  plantSpeciesId: string;
  fieldId: string;
  markedAsDeleted?: boolean; //Flag to determine if event should be deleted on save
  newEvent?: boolean; //Flag to determine event appearance in different data collection modes
  copy?: boolean; //Flag to determine if the event will be bulk copied
  createdAt?: Date;
  updatedAt?: Date;
  missingData?: boolean;
  duplicatedDate?: boolean;
  phEventOverlap?: string;
  erroredEvent?: boolean;
  ccEventOverlap?: boolean;
  missingHarvestEvent?: boolean;
}

export interface IDCHarvestEvent {
  id: string;
  plantingId: string;
  harvestDate: string;
  residueRemoval: string;
  harvestMethodId: string;
  yield: string;
  yieldUnitId: string;
  fieldId: string;
  markedAsDeleted?: boolean; //Flag to determine if event should be deleted on save
  newEvent?: boolean; //Flag to determine event appearance in different data collection modes
  copy?: boolean; //Flag to determine if the event will be bulk copied
  missingData?: boolean;
  duplicatedDate?: boolean;
  invalidHarvestDate?: boolean;
  phEventOverlap?: string;
  ccEventOverlap?: boolean;
  erroredEvent?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IDCCoverCroppingEvent {
  id: string;
  terminationMethodId: string;
  modelInputId: string;
  plantingDate: string;
  terminationDate: string;
  fieldId: string;
  species: Array<IPlantSpecies>;
  markedAsDeleted?: boolean; //Flag to determine if event should be deleted on save
  newEvent?: boolean; //Flag to determine event appearance in different data collection modes
  copy?: boolean; //Flag to determine if the event will be bulk copied
  missingData?: boolean;
  duplicatedDate?: boolean; //Determines if there is a event with the same date in the field
  phEventOverlap?: boolean; // Determines if there is a conflict with P&H event
  terminationBeforePlanting?: string; // Determines which event has it's termination date before planting date
  dateConflict?: string; // Determines which event has it's planting inside another CC
  interseeding?: boolean;
  erroredEvent?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface IDCFertilizerEvent {
  id: string;
  amount: string;
  fertilizerTypeId: string;
  dateApplied: string;
  fieldId: string;
  appliedAcreage?: number;
  markedAsDeleted?: boolean; //Flag to determine if event should be deleted on save
  newEvent?: boolean; //Flag to determine event appearance in different data collection modes
  copy?: boolean; //Flag to determine if the event will be bulk copied
  appliedGreaterThanAcreage?: boolean;
  createdAt: Date;
  updatedAt: Date;
  organicTypeId?: string;
  organicSourceId?: string;
  n?: string;
  p?: string;
  syntheticTypeId?: string;
  syntheticInhibitorId?: string;
  erroredEvent?: boolean;
  amountUnitId?: string;
  duplicatedDate?: boolean;
  missingData?: boolean;
  fertilizerTypeIdChanged?: boolean;
}

export interface IDCForageEvent {
  id: string;
  fieldId: string;
  dateOfApplication: string;
  plantSpecies: IForageSpecies[];
  speciesModelInputId: string;
  rate: number;
  areaAppliedPercent: string;
  appliedAcreage: string;
  notes: string;
  markedAsDeleted?: boolean; //Flag to determine if event should be deleted on save
  newEvent?: boolean; //Flag to determine event appearance in different data collection modes
  copy?: boolean; //Flag to determine if the event will be bulk copied
  duplicatedDate?: boolean; //Determines if there is a event with the same date in the field
  missingData?: boolean;
  erroredEvent?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IForageSpecies {
  id: string;
  name: string;
  forage: boolean;
  dominantPlant: boolean;
}

export interface IDCFieldDataEvent {
  id: string;
  fieldId: string;
  area: number;
  year: Date;
  dominantSpecies: Array<IPlantSpecies>;
  speciesModelInput: string;
  paddockNum: number;
  averagePaddockSize: string;
  notes: string;
  markedAsDeleted?: boolean; //Flag to determine if event should be deleted on save
  newEvent?: boolean; //Flag to determine event appearance in different data collection modes
  copy?: boolean; //Flag to determine if the event will be bulk copied
  erroredEvent?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IAnimalDataOperationType {
  id: string;
  name: string;
}

export interface IAnimalDataAnimalType {
  id: string;
  name: string;
}

export interface IDCAnimalDataEvent {
  id: string;
  fieldId?: string; // Not used. Kept to prevent breaking fieldId typing for other events
  herdName: string;
  year: Date;
  operationTypeId: string;
  animalTypeId: string;
  averageWeight: string;
  headCount: number;
  enclosureSize: string;
  averageDaysGrazed: number;
  totalDaysGrazed: number;
  notes: string;
  markedAsDeleted?: boolean; //Flag to determine if event should be deleted on save
  newEvent?: boolean; //Flag to determine event appearance in different data collection modes
  copy?: boolean; //Flag to determine if the event will be bulk copied
  erroredEvent?: boolean;
  missingData?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface FieldItemProps {
  showOnlyErrors: boolean;
  setShowOnlyErrors: (value: boolean) => void;
  isKeyboardUser: boolean;
  focusedElement: string;
}

export interface FieldEventItemProps {
  field: IFieldWithEvent;
  dataCollectionMode: DataCollectionModes;
  showOnlyErrors: boolean;
  isKeyboardUser: boolean;
  focusedElement: string;
}

export interface IDCEventFieldConfiguration {
  attributeName: string;
  attributeLabel: string;
  attributeType: 'DROPDOWN' | 'DATETIME' | 'INPUT' | 'MULTI_SELECT' | 'TEXTAREA';
  required: boolean;
  defaultValue: string | null;
  validate: Array<{ check?: string; type: string; validate?: (_: any, formValues: any) => boolean }>;
  dropdownOptions: Array<Record<string, any>>;
  formPosition: number;
  bounds?: string[];
  disabledDates?: string[][];
  dropdownOptionsReference?: string;
  isDisabled?: boolean;
  fertilizerType?: string;
  format?: string;
  mask?: Record<string, any>[];
  displayFertilizerType?: string;
  info?: { content: JSX.Element };
  onChange?: (event: any) => void;
}

export interface IFieldWithEvent {
  id: string;
  name: string;
  area: number;
  boundaries: any;
  numberOfEvents: number;
  hasErrors: boolean;
  practices: string[];
  tillageEvents?: Array<IDCTillageEvent>;
  plantingEvents?: Array<IDCPlantingEvent>;
  harvestEvents?: Array<IDCHarvestEvent>;
  coverCropEvents?: Array<IDCCoverCroppingEvent>;
  fieldDataEvents?: Array<IDCFieldDataEvent>;
  fertilizationEvents?: Array<IDCFertilizerEvent>;
  forageEvents?: Array<IDCForageEvent>;
}

export interface IEventsToDelete {
  name: EventTypes;
  eventIds: string[];
  fieldId: string;
}

export type DCEventTypes = Record<EventTypes, IDCEventType>;

export type EventsSuccess = {
  [type in EventTypes]: {
    [eventId: string]: IEvents;
  };
};

export interface IPracticeImplementationToUpdate {
  fieldId: string;
  implementationDate: Date;
  practiceCode: string;
}

export interface YearWithErrorCount {
  year: number;
  errors: number;
  isFinished: boolean;
}

export enum Permissions {
  EDIT = 'EDIT',
  READONLY = 'READONLY',
}

export type DatePermission = keyof typeof Permissions;
export type FieldsWithDates = Record<string, Record<string, Date>>;

export interface PermissionDetails {
  type: string;
  permission: DatePermission;
}

export enum FormTypes {
  PASTURE = 'pasture',
  ROWCROP = 'rowCrop',
}
export type FormType = (typeof FormTypes)[keyof typeof FormTypes];

export interface FormChildren {
  name: string;
  formPosition: number;
  displayFertilizerType?: string;
}

export interface FertilizerFormTypes {
  event: IDCFertilizerEvent;
  form: any;
}

export interface FinishedYearRecord {
  id: string;
  opportunityId: string;
  createdAt: Date;
  deletedAt?: Date;
}

export interface FinishedYearHistoryItem {
  journeyId: string;
  practiceType: 'ROW_CROP' | 'PASTURE';
  year: number;
  active: FinishedYearRecord | null;
  deleted: FinishedYearRecord | null;
}

export type FinishedYearHistory = FinishedYearHistoryItem[];
